// UAT 2026-05-15 — confirmation step for swapping a deal's Borrower /
// Seller / Business counterparty. Mirrors the AdminUndoConfirmModal shape:
// a red audit-visibility warning, consequence copy, and a free-text reason
// textarea server-validated to be at least 5 characters. Confirm is
// disabled until the trimmed reason meets the minimum so the 4xx round-
// trip never happens on the happy path. On confirm the caller opens the
// existing counterparty picker.

import { useEffect, useState } from "react";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import type { CounterpartyPartyType } from "@/types/deals";

export const CHANGE_COUNTERPARTY_MIN_REASON_LENGTH = 5;

interface Props {
  open: boolean;
  partyType: CounterpartyPartyType;
  onClose: () => void;
  /** Fired with the trimmed reason when the user confirms. */
  onConfirm: (reason: string) => void;
}

export function ChangeCounterpartyConfirmModal({
  open,
  partyType,
  onClose,
  onConfirm,
}: Props) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) setReason("");
  }, [open]);

  const trimmed = reason.trim();
  const reasonValid = trimmed.length >= CHANGE_COUNTERPARTY_MIN_REASON_LENGTH;
  const showLengthHint = reason.length > 0 && !reasonValid;
  const party = partyType.toLowerCase();

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Change ${partyType}?`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => reasonValid && onConfirm(trimmed)}
            disabled={!reasonValid}
          >
            CONFIRM: CHANGE
          </Button>
        </>
      }
    >
      <div className="grid gap-space-3">
        <p className="font-semibold text-status-error" role="alert">
          ⚠ This action is audit-logged and visible to compliance reviewers.
        </p>
        <p className="text-small text-gray-dark m-0">
          Changing the {party} mid-deal can invalidate signed applications,
          generated Conditional Approval letters, signed authorization certifications,
          and other downstream artifacts. Verify there is no in-flight work
          that depends on the current {party} before proceeding.
        </p>

        <label
          htmlFor="change-counterparty-reason"
          className="block mt-space-2 text-small font-display uppercase tracking-label text-gold-shadow"
        >
          Reason for change (audit-visible)
        </label>
        <textarea
          id="change-counterparty-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full border border-border-light rounded-md p-space-2 text-small focus:outline-none focus-visible:shadow-goldRing"
          placeholder={`Explain why the ${party} is changing. Min. ${CHANGE_COUNTERPARTY_MIN_REASON_LENGTH} characters.`}
          rows={3}
          aria-invalid={showLengthHint || undefined}
          aria-describedby={showLengthHint ? "change-counterparty-reason-hint" : undefined}
        />
        {showLengthHint && (
          <p
            id="change-counterparty-reason-hint"
            className="text-caption text-status-error"
            role="alert"
          >
            Reason must be at least {CHANGE_COUNTERPARTY_MIN_REASON_LENGTH} characters.
          </p>
        )}
      </div>
    </Modal>
  );
}
